import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Import as ImportIcon, Loader2, Upload, FileDown, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listVendors, importVendor } from "@/lib/api";

const VENDOR_HINTS = {
  "palo-alto": "Upload a PAN-OS running-config XML (or a fragment containing <security><rules>).",
  fortigate: "Upload a FortiGate CLI config file containing 'config firewall policy … end'.",
  checkpoint: "Upload a Check Point 'show access-rulebase' JSON export.",
  versa: "Upload a Versa Director access-policy JSON export.",
  "aws-sg": "Upload `aws ec2 describe-security-groups` JSON output.",
};

export default function Import() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    listVendors()
      .then((v) => {
        setVendors(v);
        if (v.length && !vendor) setVendor(v[0].id);
      })
      .catch(() => toast.error("Could not fetch vendor list"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (!file || !vendor) {
      toast.error("Choose a vendor and a file first");
      return;
    }
    setLoading(true);
    try {
      const data = await importVendor(vendor, file);
      setResult(data);
      toast.success(`Imported ${data.rule_count} rules from ${data.vendor_label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const blob = new Blob([result.csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `imported-${vendor}-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inspect = () => {
    // Pass the analysis result to the Dashboard via location state
    navigate("/", { state: { imported: result } });
  };

  return (
    <div className="relative">
      <div className="grid-bg absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <header className="pb-8 border-b border-[#1e293b]">
          <div className="flex items-center gap-2 text-blue-400 font-mono text-xs tracking-[0.3em] uppercase mb-3">
            <ImportIcon className="h-3.5 w-3.5" />
            <span>vendor_import</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">
            Vendor <span className="text-blue-400">Import</span>
          </h1>
          <p className="mt-4 text-slate-400 text-sm max-w-2xl">
            Paste your existing firewall export and we&apos;ll normalize it to the standard CSV,
            then run the shadow/redundant/overlap analysis in one step.
          </p>
        </header>

        <section className="pt-8 grid md:grid-cols-[280px_1fr] gap-4">
          <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-3">
              Vendor
            </div>
            <Select value={vendor} onValueChange={setVendor}>
              <SelectTrigger
                data-testid="vendor-select"
                className="bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
              >
                <SelectValue placeholder="Choose vendor" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-[#1e293b] rounded-sm">
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vendor && VENDOR_HINTS[vendor] && (
              <p className="mt-4 text-xs text-slate-500 leading-relaxed font-mono">
                {VENDOR_HINTS[vendor]}
              </p>
            )}
          </div>

          <div
            data-testid="import-dropzone"
            onClick={() => inputRef.current?.click()}
            className="border border-dashed border-[#1e293b] hover:border-blue-500/60 rounded-sm p-8 bg-[#0f172a]/60 cursor-pointer flex items-center gap-4"
            style={{ transition: "border-color 0.15s ease" }}
          >
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
              }}
            />
            <div className="h-12 w-12 border border-[#1e293b] rounded-sm flex items-center justify-center bg-[#020617]">
              <Upload className="h-5 w-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-1">
                Vendor export file
              </div>
              {file ? (
                <div className="font-mono text-sm text-slate-100 truncate">{file.name}</div>
              ) : (
                <div className="text-sm text-slate-500 font-mono">
                  Click to choose an XML / JSON / conf file
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="mt-6 flex items-center gap-3">
          <Button
            data-testid="run-import-btn"
            onClick={run}
            disabled={loading || !file || !vendor}
            className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ImportIcon className="h-4 w-4 mr-2" />
            )}
            Import & analyze
          </Button>
        </div>

        {result && (
          <section className="mt-8 space-y-4 animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                ["Imported", result.rule_count, "text-slate-100"],
                ["Shadowed", result.summary.shadowed, "text-red-400"],
                ["Redundant", result.summary.redundant, "text-amber-400"],
                ["Overlap", result.summary.overlap || 0, "text-blue-400"],
                ["Clean", result.summary.clean, "text-emerald-400"],
              ].map(([label, val, tone]) => (
                <div key={label} className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono mb-3">
                    {label}
                  </div>
                  <div className={`font-mono text-2xl font-semibold tabular-nums ${tone}`}>
                    {val}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="import-inspect-btn"
                onClick={inspect}
                className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
              >
                <ScanSearch className="h-4 w-4 mr-2" />
                Open in Inspector
              </Button>
              <Button
                data-testid="import-download-csv-btn"
                onClick={downloadCsv}
                variant="outline"
                className="rounded-sm border-[#1e293b] bg-[#0f172a] hover:bg-[#1e293b] text-slate-100"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Download normalized CSV
              </Button>
            </div>

            <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
              <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-3">
                Preview (first 15 rows)
              </div>
              <pre className="text-xs font-mono text-slate-300 overflow-x-auto whitespace-pre">
                {result.csv.split("\n").slice(0, 16).join("\n")}
              </pre>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
